import React from 'react';
import { useMediaQuery } from 'react-responsive';

import { BREAKPOINT_SIZES, BREAKPOINTS } from 'tradera/constants/breakpoints';

type BreakpointProps = {
    /**
     * LG and above
     */
    desktop?: boolean;
    /**
     * MD
     */
    tablet?: boolean;
    /**
     * SM and below
     */
    mobile?: boolean;
    /**
     * Exact breakpoint match
     */
    is?: keyof typeof BREAKPOINTS;
    /**
     * Up to (inclusive) breakpoint match
     */
    upTo?: keyof typeof BREAKPOINTS;
    /**
     * Down to (inclusive) breakpoint match
     */
    downTo?: keyof typeof BREAKPOINTS;
    children: React.ReactNode;
};

type MediaQuery = {
    minWidth?: number;
    maxWidth?: number;
};

/**
 * Conditionally render children depending on matching breakpoint
 *
 * "is" prop has precedence over "upTo" and "downTo", which can be used together to define a range
 */
export const Breakpoint = ({
    desktop,
    tablet,
    mobile,
    is: isProp,
    upTo: upToProp,
    downTo: downToProp,
    children
}: BreakpointProps) => {
    const mediaQuery: MediaQuery = {};
    let is = isProp;
    let upTo = upToProp;
    let downTo = downToProp;
    if (desktop || tablet || mobile) {
        if (desktop) downTo = BREAKPOINTS.LG;
        else if (tablet) is = BREAKPOINTS.MD;
        else if (mobile) upTo = BREAKPOINTS.SM;
    }
    if (is) {
        mediaQuery.minWidth = BREAKPOINT_SIZES[is].min;
        mediaQuery.maxWidth = BREAKPOINT_SIZES[is].max;
    } else {
        if (upTo) {
            mediaQuery.maxWidth = BREAKPOINT_SIZES[upTo].max;
        } else if (downTo) {
            mediaQuery.minWidth = BREAKPOINT_SIZES[downTo].min;
        }
    }
    const isActive = useMediaQuery(mediaQuery);
    return isActive ? <>{children}</> : null;
};

export { BREAKPOINTS };
